/**
 * Schedules Hook
 *
 * Provides data fetching and mutations for schedules
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { reportingService } from '@/services/reporting.service';
import toast from 'react-hot-toast';
import type {
  ListSchedulesQuery,
  PaginatedResponse,
  Schedule,
  CreateScheduleRequest,
  ExecutionHistoryQuery,
  ExecutionHistoryResponse,
} from '@/types/reporting-v2';

export function useSchedules(
  query: ListSchedulesQuery,
  options?: Omit<UseQueryOptions<PaginatedResponse<Schedule>['data']>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['schedules', 'list', query],
    queryFn: () => reportingService.listSchedules(query),
    staleTime: 60 * 1000, // 1 minute
    ...options,
  });
}

export function useSchedule(
  scheduleId: string | undefined,
  options?: Omit<UseQueryOptions<Schedule>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['schedules', scheduleId],
    queryFn: () => reportingService.getSchedule(scheduleId!),
    enabled: !!scheduleId,
    staleTime: 60 * 1000, // 1 minute
    ...options,
  });
}

export function useCreateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateScheduleRequest) => reportingService.createSchedule(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', 'list'] });
      toast.success('Schedule created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create schedule');
    },
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scheduleId, params }: { scheduleId: string; params: Partial<CreateScheduleRequest> }) =>
      reportingService.updateSchedule(scheduleId, params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['schedules', variables.scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['schedules', 'list'] });
      toast.success('Schedule updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update schedule');
    },
  });
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) => reportingService.deleteSchedule(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', 'list'] });
      toast.success('Schedule deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete schedule');
    },
  });
}

export function useExecuteSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scheduleId, params }: { scheduleId: string; params?: any }) =>
      reportingService.executeSchedule(scheduleId, params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['schedules', variables.scheduleId] });
      toast.success('Schedule executed successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to execute schedule');
    },
  });
}

export function usePauseSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) => reportingService.pauseSchedule(scheduleId),
    onSuccess: (_, scheduleId) => {
      queryClient.invalidateQueries({ queryKey: ['schedules', scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['schedules', 'list'] });
      toast.success('Schedule paused');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to pause schedule');
    },
  });
}

export function useResumeSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) => reportingService.resumeSchedule(scheduleId),
    onSuccess: (_, scheduleId) => {
      queryClient.invalidateQueries({ queryKey: ['schedules', scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['schedules', 'list'] });
      toast.success('Schedule resumed');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to resume schedule');
    },
  });
}

export function useExecutionHistory(
  scheduleId: string | undefined,
  query: ExecutionHistoryQuery,
  options?: Omit<UseQueryOptions<ExecutionHistoryResponse['data']>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['schedules', scheduleId, 'executions', query],
    queryFn: () => reportingService.getExecutionHistory(scheduleId!, query),
    enabled: !!scheduleId,
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  });
}
